/**
 * Animal Health · Browse (Feb 2026)
 * -----------------------------------
 * A simplified viewing surface the client asked for:
 *   Therapeutic Area  →  Disease / Subcategory  →  Matching Assets
 * Each asset opens the existing Deep Dive modal.  This page is a
 * companion to the Cohort workspace — it does NOT touch its filters
 * or state.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DeepDiveModal, AnimalDataPill } from './animalHealthShared';

const API = process.env.REACT_APP_BACKEND_URL + '/api/animal-health';


export default function AnimalHealthBrowsePage() {
  const [hierarchy, setHierarchy] = useState([]);   // [{therapeutic_area, diseases[]}]
  const [ta, setTa] = useState('');
  const [disease, setDisease] = useState('');
  const [assets, setAssets] = useState([]);
  const [loadingH, setLoadingH] = useState(true);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [ddAsset, setDdAsset] = useState(null);     // asset selected → deep-dive modal
  const [ddLoading, setDdLoading] = useState(false);

  const openDeepDive = async (a) => {
    // The Deep Dive modal expects a full assessment doc (rationale,
    // evidence, criteria maps). Fetch it lazily so the browse card
    // stays lightweight. If no assessment exists yet (not scored),
    // fall back to a minimal item — the modal still renders enough
    // for the Animal Data section to work.
    setDdLoading(true);
    try {
      const r = await axios.get(`${API}/assessment/asset/${a.id}`, { withCredentials: true });
      setDdAsset({ ...r.data, asset_name: r.data.asset_name || a.asset_name, company_name: r.data.company_name || a.biotech_company_name });
    } catch {
      setDdAsset({
        asset_id: a.id, asset_name: a.asset_name,
        company_name: a.biotech_company_name, phase: a.phase,
        primary_indication: a.primary_indication || a.indication,
        criteria: {}, key_risks: [], evidence_bullets: [], strengths: [],
      });
    } finally {
      setDdLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/browse/hierarchy`, { withCredentials: true });
        setHierarchy(r.data?.hierarchy || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load categories');
      } finally {
        setLoadingH(false);
      }
    })();
  }, []);

  const diseases = useMemo(() => {
    const g = hierarchy.find(h => h.therapeutic_area === ta);
    return g?.diseases || [];
  }, [hierarchy, ta]);

  useEffect(() => {
    if (!ta) { setAssets([]); return; }
    setLoadingAssets(true);
    (async () => {
      try {
        const params = { therapeutic_area: ta };
        if (disease) params.disease = disease;
        const r = await axios.get(`${API}/browse/assets`, { params, withCredentials: true });
        setAssets(r.data?.assets || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load assets');
      } finally {
        setLoadingAssets(false);
      }
    })();
  }, [ta, disease]);

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="ah-browse-page">
      <div className="mb-1">
        <Link
          to="/animal-health/cohort"
          className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-[12px]"
          data-testid="ah-browse-back-link"
        >
          <ArrowLeft className="w-3 h-3" /> Back to Cohort
        </Link>
      </div>

      <h1 className="text-[22px] font-semibold text-slate-900 mt-1" data-testid="ah-browse-title">
        Browse Assets by Disease
      </h1>
      <p className="text-[13px] text-slate-600 mt-1 max-w-2xl">
        Pick a Therapeutic Area, then narrow to a specific disease to see every asset
        matching that indication. Click any asset to open its Deep Dive.
      </p>

      {/* Two-dropdown selector */}
      <div className="mt-5 grid grid-cols-2 gap-4">
        <SelectField
          label="Therapeutic Area"
          value={ta}
          disabled={loadingH}
          onChange={v => { setTa(v); setDisease(''); }}
          testId="ah-browse-ta-select"
          options={hierarchy.map(h => ({
            value: h.therapeutic_area,
            label: `${h.therapeutic_area} (${h.diseases.length})`,
          }))}
          placeholder={loadingH ? 'Loading…' : 'Choose a TA'}
        />
        <SelectField
          label="Disease / Subcategory"
          value={disease}
          disabled={!ta || diseases.length === 0}
          onChange={setDisease}
          testId="ah-browse-disease-select"
          options={diseases.map(d => ({ value: d, label: d }))}
          placeholder={!ta ? 'Pick a TA first' : diseases.length === 0 ? 'No diseases in this TA' : 'All diseases'}
          allowClear
        />
      </div>

      {/* Path indicator */}
      {ta && (
        <div className="mt-4 text-[12px] text-slate-600 flex items-center gap-1.5" data-testid="ah-browse-breadcrumb">
          <span className="font-medium text-slate-800">{ta}</span>
          {disease && (<><ChevronRight className="w-3 h-3 text-slate-400" /><span className="font-medium text-slate-800">{disease}</span></>)}
          <ChevronRight className="w-3 h-3 text-slate-400" />
          <span className="text-slate-500">{loadingAssets ? '…' : `${assets.length} assets`}</span>
        </div>
      )}

      {/* Asset list */}
      <div className="mt-4 border rounded-lg bg-white overflow-hidden" data-testid="ah-browse-list">
        {loadingAssets ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading assets…
          </div>
        ) : !ta ? (
          <div className="p-10 text-center text-slate-400 text-[13px]">
            Choose a Therapeutic Area above to start browsing.
          </div>
        ) : assets.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-[13px]">
            No assets found matching this selection.
          </div>
        ) : (
          <ul className="divide-y">
            {assets.map(a => (
              <li
                key={a.id}
                onClick={() => openDeepDive(a)}
                className="p-3.5 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                data-testid={`ah-browse-asset-${a.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-medium text-slate-900 truncate">
                    {a.asset_name || <em className="text-slate-400">Unnamed asset</em>}
                  </div>
                  <div className="text-[11.5px] text-slate-500 truncate">
                    {a.biotech_company_name || '—'}
                    {a.phase ? ` · ${a.phase}` : ''}
                    {a.primary_indication ? ` · ${a.primary_indication}` : (a.indication ? ` · ${a.indication}` : '')}
                  </div>
                </div>
                <AnimalDataPill status={a.animal_data_status} />
                <Button size="sm" variant="ghost" className="h-7 text-[11px]">
                  <Sparkles className="w-3 h-3 mr-1" /> Deep Dive
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {ddAsset && (
        <DeepDiveModal
          open={!!ddAsset}
          item={ddAsset}
          onClose={() => setDdAsset(null)}
        />
      )}
    </div>
  );
}


function SelectField({ label, value, onChange, options, placeholder, disabled, allowClear, testId }) {
  return (
    <label className="block">
      <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1">{label}</div>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        className="w-full h-9 px-3 rounded border border-slate-300 bg-white text-[13px] disabled:bg-slate-50 disabled:text-slate-400"
        data-testid={testId}
      >
        <option value="">{allowClear ? placeholder : placeholder}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}
